import React, { useCallback, useState } from 'react';

import { isElementInViewport } from '../utils/isElementInViewport';

import FaqArticlesItem from './FaqArticlesItem';
import { IArticleResponse, IFaqArticle } from './ICustomerService';
import { ICustomerServiceApi } from './ICustomerServiceApi';
import MinusIcon from './MinusIcon';
import PlusIcon from './PlusIcon';

export interface FaqArticlesProps {
    articles: IFaqArticle[];
    api: ICustomerServiceApi;
}

const FaqArticles = ({ articles, api }: FaqArticlesProps) => {
    const UI_UPDATE_ALLOWANCE_MS = 10;

    const [faqArticles, setFaqArticles] = useState<Record<number, IArticleResponse>>({});
    const [isFetching, setIsFetching] = useState(false);
    const [expandedDrawerIndex, setExpandedDrawerIndex] = useState(-1);

    const isDrawerOpen = useCallback(
        (i: number) => {
            return i === expandedDrawerIndex;
        },
        [expandedDrawerIndex],
    );

    const checkFaqArticleFetched = (i: number) => {
        if (faqArticles[i]) {
            return true;
        }

        return false;
    };

    const onFaqClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, i: number) => {
        e.preventDefault();
        const current = i === expandedDrawerIndex;
        const el = document.querySelectorAll<HTMLElement>('.customer-service-faq__faq-title')[i];
        // const offsetBeforeCollapse = el.getBoundingClientRect().top;
        setExpandedDrawerIndex((prev) => (prev !== i ? i : -1));

        if (!current) {
            if (!checkFaqArticleFetched(i)) {
                setIsFetching(true);
                const faqArticle = await api.getArticle(articles[i].UrlName);
                setFaqArticles((prev) => {
                    return {
                        ...prev,
                        [i]: faqArticle,
                    };
                });
                setIsFetching(false);
            }
        }

        // Before this code block runs, React has to make a UI update.
        // To re-render, function execution should finish.
        // Set timeout allows React to initiate re-render.
        // After React updates the UI, this code block runs.
        // Offset values should reflect the new window state
        setTimeout(() => {
            const shouldExpand = !current;
            const inViewport = isElementInViewport(el);

            if (shouldExpand && !inViewport) {
                const elementOffsetRelativeToPageStart = el.offsetTop;
                const windowNewScrollTopValue = elementOffsetRelativeToPageStart;
                const currentWindowHorizontalScrollValue = window.scrollY;

                window.scrollTo({
                    left: currentWindowHorizontalScrollValue,
                    top: windowNewScrollTopValue,
                    behavior: 'smooth',
                });
            }
        }, UI_UPDATE_ALLOWANCE_MS);
    };

    return (
        <ul className="customer-service-faq__faq">
            {articles.map((faqArticle, i) => (
                <li className="customer-service-faq__faq-title-container" key={faqArticle.UrlName}>
                    <button className="customer-service-faq__faq-title" onClick={(e) => onFaqClick(e, i)}>
                        <span className="customer-service-faq__faq-title-text">{faqArticle.FaqDisplayTitle}</span>
                        <div className="customer-service-faq__faq-title-expand">
                            {isDrawerOpen(i) ? <MinusIcon /> : <PlusIcon />}
                        </div>
                    </button>
                    {isDrawerOpen(i) && (
                        <div className="customer-service-faq__faq-article">
                            {isFetching && <div></div>}
                            {!isFetching && <FaqArticlesItem article={faqArticles[i]} />}
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );
};

export default FaqArticles;
